/* [서비스 가치(Service Value)] 글로벌 Context 데이터와 컴포넌트 재사용성을 극대화한 SPA 엔트리 포인트.
   🚀 CTO 패치: 
   1. 메뉴명 동적 분기: 'CareReportManager'를 학생/학부모에게는 [출결 상황]으로, 강사/관리자에게는 [원생별 출결관리]로 분리하여 노출합니다.
   2. 통합 출결 관리 최적화: '통합 출결 관리'의 설명을 일별 관제 및 매트릭스 전용으로 수정했습니다.
   3. 온톨로지 맵 통합: 'AI 수학 지식 맵'을 Lazy Loading으로 주입하여 초기 로딩 속도(LCP) 저하 없이 상담용 시각화 툴을 제공합니다. */

import React, { useState, Suspense, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';

import { 
  Home, Calendar as CalendarIcon, Settings, LogOut, Menu, X, CheckCircle, Eye, EyeOff, AlertCircle, 
  Video, Loader, DollarSign, Briefcase, Printer, BookOpen, User, Target, Compass, FileText, Activity,
  Clock, Trash2, MessageSquare, Globe, Phone, Search, Clipboard, Book, Users, Star, ChevronDown, ChevronRight,
  PieChart, UserPlus, UserCheck, Brain, GraduationCap, Sparkles, CalendarDays, Share2, ClipboardList, ShoppingBag, Boxes, Plus 
} from 'lucide-react'; // 🚀 CTO Patch: 온톨로지 맵을 위한 Share2 아이콘 추가

import { doc, getDoc } from 'firebase/firestore';
import { signInWithEmailAndPassword, signInWithCustomToken, signOut, onAuthStateChanged } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from './firebase';
import { DataProvider, useData } from './contexts/DataContext';
import SmartSchoolSelect from './components/SmartSchoolSelect';
import ErrorBoundary from './components/ErrorBoundary';
import { toStorableSchoolName } from './utils/schoolName';
import { toMainSubject, MAIN_SUBJECTS } from './utils/subjectMatch';
import { APP_ID } from './constants';
import TodaySummary from './features/TodaySummary';   // 대시보드 '오늘' 요약 — 첫 화면이라 지연 로딩하지 않는다
import emblemWhite from './assets/emblem-white.png';   // 학원 날개 엠블럼(흰색) — 파란 사각형 안에 넣는 로고

// 컴포넌트 Lazy 로딩
const ClinicDashboard = React.lazy(() => import('./features/ClinicDashboard'));
const ClinicTaskManager = React.lazy(() => import('./features/ClinicTaskManager'));
const CallRecords = React.lazy(() => import('./features/CallRecords'));
const AdminLectureManager = React.lazy(() => import('./features/LectureManager').then(module => ({ default: module.AdminLectureManager })));
const LecturerDashboard = React.lazy(() => import('./features/LectureManager').then(module => ({ default: module.LecturerDashboard })));
const StudentClassroom = React.lazy(() => import('./features/StudentClassroom'));
const UserManager = React.lazy(() => import('./features/UserManager'));
const PayrollManager = React.lazy(() => import('./features/PayrollManager'));
const PickupRequest = React.lazy(() => import('./features/PickupRequest'));
const ExamArchive = React.lazy(() => import('./features/ExamArchive'));
const TextbookManager = React.lazy(() => import('./features/TextbookManager'));
const HomeworkManager = React.lazy(() => import('./features/HomeworkManager'));
const SchoolStrategy = React.lazy(() => import('./features/SchoolStrategy'));
const ExamDiagnosticInput = React.lazy(() => import('./features/ExamDiagnosticInput'));
const ExamDiagnosticReport = React.lazy(() => import('./features/ExamDiagnosticReport'));
const StudentExamList = React.lazy(() => import('./features/StudentExamList'));
const ExpenseManager = React.lazy(() => import('./features/ExpenseManager'));
const FinancialDashboard = React.lazy(() => import('./features/FinancialDashboard'));
const AttendanceManager = React.lazy(() => import('./features/AttendanceManager'));
const SettingsManager = React.lazy(() => import('./features/SettingsManager'));
const MessageCenter = React.lazy(() => import('./features/MessageCenter'));
const CollegeNavigator = React.lazy(() => import('./features/CollegeNavigator'));
const AcademyUniverse = React.lazy(() => import('./features/AcademyUniverse'));
const ConsultationManager = React.lazy(() => import('./features/ConsultationManager'));
const VocaManager = React.lazy(() => import('./features/VocaManager'));
const StudentVocaDaily = React.lazy(() => import('./features/StudentVocaDaily'));
const VocaChallenge = React.lazy(() => import('./features/VocaChallenge'));
const StudentDashboard = React.lazy(() => import('./features/StudentDashboard'));
const RecruitmentManager = React.lazy(() => import('./features/RecruitmentManager'));
const AcademicCalendarManager = React.lazy(() => import('./features/AcademicCalendarManager'));
const CareReportManager = React.lazy(() => import('./features/CareReportManager'));
// 교재 구매 연동: 학생·학부모는 [내 교재], 교직원은 [교재 주문 관리]
const BookOrders = React.lazy(() => import('./features/BookOrders'));
// 교재 발주 관리: 결제된 교재를 모아 서점에 주문하고 도착·입고까지 기록 (지금은 관리자만 — src/utils/bookPurchases.js PURCHASE_ROLES)
const BookPurchases = React.lazy(() => import('./features/BookPurchases'));
// 🚀 CTO Patch: OntologyMap Lazy 로딩 추가 (초기 로딩 최적화로 이탈률 0% 방어)
const OntologyMap = React.lazy(() => import('./features/ontology/OntologyMap'));
// 📊 성적 분석 허브: 연동 진단 + 분석 스튜디오 14기능을 하위 탭으로 (관리자 전용)
// ⚠ 로컬 점검용 이식 (2026-08-23) — docs/admission-center/PLAN.md 승인 전까지 커밋·배포 금지
const GradeAnalysisHub = React.lazy(() => import('./features/GradeAnalysisHub'));


/* 로그인 아이디를 Firebase Auth 이메일로 쓸 수 있는 안전한 문자열로 변환합니다.
   서버(functions/index.js)의 toSafeId와 반드시 동일한 규칙이어야 합니다. */
const toSafeId = (raw) =>
  encodeURIComponent(String(raw || '')).replace(/[^a-zA-Z0-9]/g, 'x').toLowerCase();

// 메뉴 생성 함수 (권한에 따라 동적으로 메뉴명을 할당합니다)
const buildMenuGroups = (currentUser) => [
    {
        title: "나의 학원 생활",
        description: "오늘의 스케줄과 AI 브리핑을 확인합니다.",
        theme: "from-indigo-600 to-blue-700",
        items: [
            { name: "My Imperial Day", path: "/my-day", icon: Sparkles, desc: "오늘의 완벽한 스케줄과 AI 멘토링", roles: ['student'] },
            { name: "출결 상황", path: "/care-report", icon: PieChart, desc: "학원의 밀착 케어 내역과 출결 통계", roles: ['student', 'parent'] }
        ]
    },
    {
        title: "교무 및 출결 관제",
        description: "클리닉 예약, 출결 현황 및 교실 공간을 관리합니다.",
        theme: "from-blue-600 to-indigo-700",
        items: [
            { name: "신규 상담 등록", path: "/consult", icon: UserPlus, desc: "신규 원생 상담 데이터를 입력합니다.", roles: ['admin', 'admin_assistant', 'lecturer'] },
            { name: "통합 출결 관리", path: "/attendance", icon: UserCheck, desc: "당일 출결 관제 및 교실 매트릭스", roles: ['admin', 'admin_assistant', 'lecturer'] },
            { name: "원생별 출결관리", path: "/care-report", icon: PieChart, desc: "원생별 누적 학습 시간 및 출결 데이터", roles: ['admin', 'admin_assistant', 'lecturer', 'ta'] },
            { name: "학사일정 마스터", path: "/academic-calendar", icon: CalendarDays, desc: "학교별 학사일정 및 D-Day 관리", roles: ['admin', 'admin_assistant'] },
            { name: "클리닉 센터", path: "/clinic", icon: CalendarIcon, desc: "1:N 클리닉 배정 및 예약 현황", roles: ['admin', 'admin_assistant', 'lecturer', 'ta', 'student', 'parent'] },
            { name: "오늘의 할 일", path: "/clinic-tasks", icon: Clipboard, desc: "조교 업무 지시 및 진행률 트래킹", roles: ['admin', 'admin_assistant', 'ta', 'lecturer'] },
            { name: "픽업 신청", path: "/pickup", icon: Printer, desc: "학생 픽업을 요청하고 관리합니다.", roles: ['lecturer'] }
        ]
    },
    {
        title: "학습 및 수강",
        description: "학생들의 성적, 수업, 학습 자료를 관리합니다.",
        theme: "from-emerald-600 to-teal-700",
        items: [
            { name: "강의 관리", studentName: "수강 강의", path: "/lectures", icon: Video, studentIcon: GraduationCap, desc: "수업 진도, 숙제 관리 및 영상 시청", roles: ['admin', 'admin_assistant', 'lecturer', 'ta', 'student', 'parent'] },
            /* 학생·학부모의 [내 교재] (반에 지정된 교재 안내 → 북스에서 결제). 교직원용 주문·발주 관리는 아래 '북스 관리' 묶음에 있다. */
            { name: "내 교재", path: "/books", icon: ShoppingBag, studentIcon: ShoppingBag, desc: "수업 교재 확인 · 구매 · 수령 현황", roles: ['student', 'parent'] },
            { name: "내신 연구소", path: "/strategy", icon: Brain, desc: "학교별 맞춤형 출제 경향 및 리포트", roles: ['admin', 'admin_assistant', 'lecturer', 'ta', 'student', 'parent'] },
            // 🚀 CTO Patch: 상담 전환율을 극대화하는 AI 수학 지식 맵 메뉴 추가 (모든 역할군 오픈)
            { name: "AI 수학 지식 맵", path: "/ontology", icon: Share2, desc: "수학 커리큘럼 선행 및 취약점 시각화", roles: ['admin', 'admin_assistant', 'lecturer', 'ta', 'student', 'parent'] },
            { name: "기출 아카이브", path: "/exams", icon: BookOpen, desc: "학교별 기출문제 은행", roles: ['admin', 'admin_assistant', 'lecturer', 'ta'] },
            { name: "교재 관리", path: "/textbooks", icon: Book, desc: "문제집 범위·단원 등록 (숙제 배정용)", roles: ['admin', 'admin_assistant', 'lecturer', 'ta'] },
            { name: "숙제 관리", path: "/homework", icon: ClipboardList, desc: "교재에서 숙제 출제 및 오답 채점", roles: ['admin', 'admin_assistant', 'lecturer', 'ta'] },
            { name: "Voca 출제/관리", studentName: "오늘의 영단어", path: "/voca", icon: Book, desc: "맞춤형 단어장 및 고속 채점 시스템", roles: ['admin', 'admin_assistant', 'lecturer', 'ta', 'student', 'parent'], condition: (u) => !['lecturer', 'ta'].includes(u?.role) || toMainSubject(u?.subject) === '영어' },
            { name: "영단어 챌린지", path: "/voca-challenge", icon: Star, desc: "게이미피케이션 기반 단어 암기", roles: ['admin', 'admin_assistant', 'lecturer', 'ta', 'student'] },
            { name: "시험 진단 입력", path: "/exam-diagnostics", icon: Target, desc: "시험 결과를 입력하고 리포트 생성", roles: ['admin', 'admin_assistant', 'lecturer', 'ta'] },
            { name: "나의 시험 결과", path: "/my-exams", icon: Target, desc: "성적표 및 담당 선생님 리포트", roles: ['student', 'parent'] }
        ]
    },
    {
        // ⚠ 로컬 점검용 상위 탭 (2026-08-23) — PLAN 승인 전까지 커밋·배포 금지
        // 사이드바 항목이 곧 기능이다 — 순서는 상담 흐름(학생 → 성적 → 진단 → 생기부 → 리포트).
        title: "진로진학",
        description: "입시 내비게이터와 내신·모의고사 연동 진단, 수시·정시 전략을 한 화면에서.",
        theme: "from-violet-600 to-indigo-700",
        items: [
            /* 원장 지시(08-23): 성적입력·학생관리는 기존 시스템이 하고, 대시보드·모의고사
               분석은 제외(모의고사 내용은 추후 기존 성적입력 화면에서 활용 예정).
               생기부 진단·OMR 채점 탭도 제외.
               탭 재편(08-24 원장 승인 B안): 11개 → 5개. 수시·정시·전문대는 '지원 진단'
               상단 전환으로, 등급 변환기는 그 안의 도구 버튼으로, 탐구+과목은 '학종 설계'로,
               등급컷+성취도는 '기준 데이터'로 묶음(GradeAnalysisHub GROUPS). 일정 캘린더는
               학사일정 마스터의 '입시 일정' 레이어 + 수시 진단 카트의 '일정 보기'로 이동.
               08-24: 그룹명 성적 분석 → 진로진학, 입시 내비게이터를 학습 그룹에서 이리로
               옮겨 첫 항목으로 (학생·학부모 접근 권한은 그대로). */
            /* 08-24 원장 지시: 성적 연동 진단 탭 삭제(입시 내비게이터가 그 역할),
               지원 진단 → 시뮬레이터로 개명. 내비게이터에서 학생을 고르면 그 학생이
               스튜디오(시뮬레이터·학종 설계·종합 리포트)의 활성 학생으로 자동 연동된다. */
            /* 원장 지시(08-30): 진로진학 탭 전체를 관리자 계정만 — 새 진단 DB·추천이 검수 전이라
               학생·학부모에게는 아직 적용하지 않는다. 검수가 끝나면 student·parent 를 되돌린다. */
            { name: "입시 내비게이터", path: "/navigator", icon: Compass, desc: "성적 추이 분석 및 목표 대학 전략", roles: ['admin'] },
            { name: "시뮬레이터", path: "/grade-center/apply", icon: Target, desc: "수시·정시·전문대 판정 + 등급 변환기", roles: ['admin'] },
            { name: "학종 설계", path: "/grade-center/roadmap", icon: Book, desc: "탐구 주제 DB · 고교학점제 과목 설계", roles: ['admin'] },
            { name: "기준 데이터", path: "/grade-center/data", icon: Clipboard, desc: "모의고사 등급컷 · 성취도 반영 정책", roles: ['admin'] },
            { name: "종합 리포트", path: "/grade-center/report", icon: Printer, desc: "학부모 면담용 한 부 인쇄", roles: ['admin'] }
        ]
    },
    {
        title: "재무 및 행정",
        description: "수납, 급여, 시스템 설정을 관리합니다.",
        theme: "from-amber-500 to-orange-600",
        items: [
            { name: "재무 대시보드", path: "/financial-dashboard", icon: PieChart, desc: "자금 흐름 파악 및 결재 승인", roles: ['admin'] },
            { name: "지출결의 등록", path: "/expense", icon: FileText, desc: "지출 내역 등록 및 증빙 업로드", roles: ['admin', 'admin_assistant', 'lecturer', 'ta'] },
            { name: "월급 정산 관리", path: "/payroll-mgmt", icon: Briefcase, desc: "전체 직원의 급여 정산 및 관리", roles: ['admin'] },
            { name: "내 월급 확인", path: "/payroll-check", icon: DollarSign, desc: "이번 달 급여 명세서 확인", roles: ['admin', 'admin_assistant', 'lecturer', 'ta'] },
            { name: "사용자 관리", path: "/users", icon: Users, desc: "모든 계정 승인 및 권한 관리", roles: ['admin', 'admin_assistant'] },
            { name: "채용 및 인사 관리", path: "/recruitment", icon: Briefcase, desc: "조교/강사 채용 및 온보딩 파이프라인", roles: ['admin', 'admin_assistant'] }
        ]
    },
    {
        /* 임페리얼 북스(별도 사업자) 업무. 결제는 북스 사이트에서 하고, 학원 시스템은 주문 전달·발주·입고만 관리한다.
           교재 발주 관리는 지금 관리자만 — 데스크 실장님·행정조교에게 열 때 roles 와 PURCHASE_ROLES·firestore.rules 를 함께 바꾼다. */
        title: "북스 관리",
        description: "임페리얼 북스 교재 주문 전달·발주·입고를 관리합니다.",
        theme: "from-amber-700 to-stone-900",
        items: [
            { name: "교재 주문 관리", path: "/books", icon: ShoppingBag, desc: "반별 교재 구매 현황 · 주문 전달 · 환불 · 정산", roles: ['admin', 'admin_assistant'] },
            { name: "교재 발주 관리", path: "/book-purchases", icon: Boxes, desc: "주문할 교재 모아 보기 · 발주 · 도착 · 입고 관리", roles: ['admin'] }
        ]
    },
    {
        title: "학원 생활 및 시스템",
        description: "알림, 메시지, 스케줄을 확인합니다.",
        theme: "from-slate-700 to-slate-900",
        items: [
            { name: "아카데미 유니버스", path: "/universe", icon: Globe, desc: "학원 소식 및 커뮤니티 랭킹", roles: ['admin', 'admin_assistant', 'lecturer', 'ta', 'student', 'parent'] },
            { name: "통합 메시지 센터", path: "/messages", icon: MessageSquare, desc: "알림톡/SMS 발송 및 관리", roles: ['admin', 'admin_assistant'] },
            { name: "통화 기록", path: "/call-records", icon: Phone, desc: "법인폰 통화 자동 요약 (원장 전용)", roles: ['admin'] },
            { name: "근무 스케줄", path: "/work-schedule", icon: Clock, desc: "나의 근무 일정 확인 및 변경 요청", roles: ['admin_assistant'] },
            { name: "환경 설정", path: "/settings", icon: Settings, desc: "학원 시스템 코어 데이터 설정", roles: ['admin'] }
        ]
    }
];

/* 학생·학부모에게는 교직원용 묶음 이름 대신 쉬운 이름을 보인다(11-02 학생·학부모 점검 —
   클리닉 센터 하나가 '교무 및 출결 관제' 아래에 있었다). 사이드바와 대시보드가 같이 쓴다. */
const FAMILY_GROUP_TITLES = { '교무 및 출결 관제': '클리닉', '학원 생활 및 시스템': '학원 소식' };
const getMenuGroups = (currentUser) => {
    const groups = buildMenuGroups(currentUser);
    if (!['student', 'parent'].includes(currentUser?.role)) return groups;
    return groups.map(g => (FAMILY_GROUP_TITLES[g.title] ? { ...g, title: FAMILY_GROUP_TITLES[g.title] } : g));
};

const ReportWrapper = () => {
  const { diagnosticId } = useParams();
  return <ExamDiagnosticReport diagnosticId={diagnosticId} />;
};


const SignUpForm = ({ onCancel, setLoginErrorModal }) => {
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false); 
    const [form, setForm] = useState({ role: 'student', userId: '', password: '', name: '', phone: '', schoolName: '', grade: '1학년', childName: '', subject: '' });
    const [smsAuth, setSmsAuth] = useState({ ticket: '', input: '', sent: false, verified: false, timer: 0 });
    const [schoolsData, setSchoolsData] = useState({ elementary: [], middle: [], high: [], favorites: [] });
    const [schoolType, setSchoolType] = useState('high');
    const [isCustomSchool, setIsCustomSchool] = useState(false); 

    useEffect(() => {
        const fetchSchools = async () => {
            try {
                const docRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'settings', 'schools');
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) setSchoolsData(docSnap.data());
            } catch (e) { console.error(e); }
        };
        fetchSchools();
    }, []);

    useEffect(() => {
        let interval = null;
        if (smsAuth.timer > 0 && !smsAuth.verified) {
            interval = setInterval(() => { setSmsAuth(prev => ({ ...prev, timer: prev.timer - 1 })); }, 1000);
        } else if (smsAuth.timer === 0 && smsAuth.sent && !smsAuth.verified) {
            setSmsAuth(prev => ({ ...prev, sent: false }));
            setLoginErrorModal({ isOpen: true, msg: "인증 시간이 만료되었습니다. 다시 시도해주세요." });
        }
        return () => clearInterval(interval);
    }, [smsAuth.timer, smsAuth.sent, smsAuth.verified, setLoginErrorModal]);

    /* 🔒 인증번호는 서버가 발급하고 서버가 채점합니다.
       (기존에는 브라우저가 번호를 만들고 브라우저가 비교해서 우회가 가능했습니다) */
    const handleSendAuthCode = async () => {
        const cleanPhone = form.phone.replace(/[^0-9]/g, '');
        if (cleanPhone.length < 10) return setLoginErrorModal({ isOpen: true, msg: '유효한 휴대폰 번호를 입력해주세요.' });
        setLoading(true);
        try {
            const requestCode = httpsCallable(functions, 'requestSignupCode');
            await requestCode({ phone: cleanPhone, name: form.name || '신규가입자' });
            setSmsAuth({ ticket: '', input: '', sent: true, verified: false, timer: 180 });
            alert('인증번호가 발송되었습니다.');
        } catch (error) {
            setLoginErrorModal({ isOpen: true, msg: '인증번호 발송 실패: ' + (error.message || '잠시 후 다시 시도해주세요.') });
        } finally { setLoading(false); }
    };

    const handleVerifyCode = async () => {
        const cleanPhone = form.phone.replace(/[^0-9]/g, '');
        setLoading(true);
        try {
            const verifyCode = httpsCallable(functions, 'verifySignupCode');
            const res = await verifyCode({ phone: cleanPhone, code: smsAuth.input });
            if (res?.data?.verified) {
                setSmsAuth(prev => ({ ...prev, verified: true, ticket: res.data.ticket }));
            } else {
                setLoginErrorModal({ isOpen: true, msg: '인증번호가 일치하지 않습니다.' });
            }
        } catch (error) {
            setLoginErrorModal({ isOpen: true, msg: error.message || '인증번호가 일치하지 않습니다.' });
        } finally { setLoading(false); }
    };

    const getGradeOptions = (type) => {
        if (type === 'elementary') return ['1학년','2학년','3학년','4학년','5학년','6학년'];
        if (type === 'middle') return ['1학년','2학년','3학년'];
        return ['1학년','2학년','3학년','N수생'];
    };

    const handleSignUp = async (e) => {
        e.preventDefault();
        if (!smsAuth.verified) return setLoginErrorModal({ isOpen: true, msg: '먼저 휴대폰 본인 인증을 완료해주세요.' });
        if (!form.userId || !form.password || !form.name) return setLoginErrorModal({ isOpen: true, msg: '필수 정보를 모두 입력해주세요.' });
        if (form.password.length < 6) return setLoginErrorModal({ isOpen: true, msg: '비밀번호는 6자리 이상이어야 합니다.' });
        if (['student', 'parent'].includes(form.role) && !form.schoolName) return setLoginErrorModal({ isOpen: true, msg: '학교를 정확히 선택하거나 입력해주세요.' });
        if (form.role === 'parent' && !form.childName) return setLoginErrorModal({ isOpen: true, msg: '자녀 이름을 입력해주세요.' });
        if (['ta', 'lecturer'].includes(form.role) && !form.subject) return setLoginErrorModal({ isOpen: true, msg: '담당 과목을 선택해주세요.' });

        setLoading(true);
        try {
            /* 🔒 계정 생성은 서버가 담당합니다.
               비밀번호는 Firebase Auth에만 저장되고 Firestore에는 남지 않습니다. */
            const register = httpsCallable(functions, 'registerUser');
            await register({
                ticket: smsAuth.ticket,
                phone: form.phone.replace(/[^0-9]/g, ''),
                userId: form.userId,
                password: form.password,
                name: form.name,
                role: form.role,
                // 마스터 목록에 있는 학교면 그 정식 명칭으로 통일해 저장합니다.
                schoolName: toStorableSchoolName(form.schoolName, schoolsData),
                grade: form.grade,
                childName: form.childName,
                subject: form.subject
            });
            alert('가입 신청이 완료되었습니다. 데스크 승인 후 로그인 가능합니다.');
            onCancel();
        } catch (error) {
            setLoginErrorModal({ isOpen: true, msg: error.message || '가입 처리 중 오류가 발생했습니다.' });
        } finally { setLoading(false); }
    };

    return (
        <form onSubmit={handleSignUp} className="space-y-4 animate-in fade-in">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">시스템 회원가입</h2>
                <button type="button" onClick={onCancel} className="p-2 bg-gray-100 rounded-full hover:bg-gray-200"><X size={20}/></button>
            </div>
            <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5 ml-1">가입 유형</label>
                <select className="w-full border rounded-xl p-3 bg-gray-50 font-bold" value={form.role} onChange={e => setForm({...form, role: e.target.value})}>
                    {/* 교직원(강사·조교) 계정은 직접 가입할 수 없습니다.
                        예전에는 여기서 '강사'를 고르면 서버가 그대로 믿고 교직원 권한을 줬습니다.
                        교직원 계정은 데스크가 [직원 관리]에서 발급합니다. */}
                    <option value="student">학생</option>
                    <option value="parent">학부모</option>
                </select>
                <p className="text-xs text-gray-500 mt-1.5 ml-1">
                    강사·조교 계정은 학원 데스크에서 발급해 드립니다.
                </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1.5 ml-1">이름</label>
                    <input required className="w-full border p-3 rounded-xl bg-gray-50 font-bold" placeholder="실명 입력" value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
                </div>
                <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1.5 ml-1">아이디</label>
                    <input required className="w-full border p-3 rounded-xl bg-gray-50 font-bold" placeholder="영문/숫자" value={form.userId} onChange={e => setForm({...form, userId: e.target.value})} />
                </div>
            </div>
            <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5 ml-1">비밀번호</label>
                <div className="relative">
                    <input required type={showPassword ? "text" : "password"} placeholder="6자리 이상" className="w-full border p-3 rounded-xl bg-gray-50 font-bold" value={form.password} onChange={e => setForm({...form, password: e.target.value})} />
                    <button type="button" className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" onClick={() => setShowPassword(!showPassword)}>{showPassword ? <EyeOff size={20} /> : <Eye size={20} />}</button>
                </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 space-y-3">
                <label className="block text-xs font-bold text-blue-800 flex items-center gap-1"><Phone size={14}/> 휴대폰 본인 인증</label>
                <div className="flex gap-2">
                    <input className="w-full border p-3 rounded-xl font-bold bg-white" placeholder="01012345678 (-없이)" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} disabled={smsAuth.verified} />
                    <button type="button" onClick={handleSendAuthCode} disabled={loading || smsAuth.verified || smsAuth.timer > 0} className="shrink-0 bg-blue-600 text-white rounded-xl font-bold px-4">{smsAuth.timer > 0 ? '재전송' : '인증번호 받기'}</button>
                </div>
                {smsAuth.sent && !smsAuth.verified && (
                    <div className="flex gap-2 animate-in slide-in-from-top-2">
                        <div className="relative w-full">
                            <input className="w-full border-2 border-indigo-200 p-3 rounded-xl font-black text-center tracking-widest bg-white" placeholder="인증번호 6자리" value={smsAuth.input} onChange={e => setSmsAuth({...smsAuth, input: e.target.value})} />
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-rose-500 font-bold text-sm flex items-center gap-1"><Clock size={14}/> {Math.floor(smsAuth.timer / 60)}:{String(smsAuth.timer % 60).padStart(2, '0')}</div>
                        </div>
                        <button type="button" onClick={handleVerifyCode} className="shrink-0 bg-indigo-600 text-white rounded-xl font-bold px-4">확인</button>
                    </div>
                )}
                {smsAuth.verified && <div className="text-sm font-bold text-emerald-600 flex items-center gap-1"><CheckCircle size={16}/> 인증이 완료되었습니다.</div>}
            </div>

            {['student', 'parent'].includes(form.role) && (
                <div className="grid grid-cols-1 gap-3 bg-gray-50 p-4 rounded-xl border border-gray-200">
                    {form.role === 'parent' && (
                        <div className="mb-2">
                            <label className="block text-xs font-bold text-gray-500 mb-1.5 ml-1">자녀 이름</label>
                            <input required className="w-full border-2 border-gray-200 focus:border-blue-400 outline-none p-3 rounded-xl bg-white font-bold transition-colors" placeholder="자녀 실명 입력" value={form.childName} onChange={e => setForm({...form, childName: e.target.value})} />
                            <p className="text-[11px] text-rose-500 font-bold ml-1 mt-1.5">* 2명 이상의 자녀가 재원 중인 경우, 가입 후 데스크에 문의 바랍니다.</p>
                        </div>
                    )}
                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1.5 ml-1">{form.role === 'parent' ? '자녀 학교 정보' : '학교 정보'}</label>
                        <div className="flex gap-2 relative">
                            <select className="w-1/3 border-2 border-gray-200 p-3 rounded-xl bg-white font-bold text-sm outline-none focus:border-blue-400 transition-colors" value={schoolType} onChange={e => { setSchoolType(e.target.value); setForm({...form, schoolName: '', grade: '1학년'}); setIsCustomSchool(false); }}>
                                <option value="elementary">초등학교</option>
                                <option value="middle">중학교</option>
                                <option value="high">고등학교</option>
                            </select>
                            {/* 회원가입은 로그인 전이라 학교 마스터 목록에 쓸 권한이 없습니다.
                                그래서 여기서는 '새 학교로 추가'를 막고 기존 직접 입력을 남기되,
                                저장 시점에 목록의 정식 명칭으로 맞춥니다(handleSignUp 참고). */}
                            {!isCustomSchool ? (
                                <SmartSchoolSelect size="lg" className="w-2/3" allowAddNew={false} schoolType={schoolType} schoolsData={schoolsData} value={form.schoolName} onChange={(val) => setForm({...form, schoolName: val})} onCustomSelect={() => setIsCustomSchool(true)}/>
                            ) : (
                                <div className="w-2/3 relative">
                                    <input required className="w-full border-2 border-blue-400 p-3 rounded-xl bg-white font-bold text-sm pr-8 outline-none" placeholder="학교명 직접 입력" value={form.schoolName} onChange={e => setForm({...form, schoolName: e.target.value})} />
                                    <button type="button" onClick={() => { setIsCustomSchool(false); setForm({...form, schoolName: ''}); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 bg-white rounded-full p-1 transition-colors"><X size={16}/></button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1.5 ml-1">{form.role === 'parent' ? '자녀 학년' : '학년'}</label>
                        <select className="w-full border-2 border-gray-200 focus:border-blue-400 outline-none p-3 rounded-xl bg-white font-bold transition-colors" value={form.grade} onChange={e => setForm({...form, grade: e.target.value})}>
                            {getGradeOptions(schoolType).map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                    </div>
                </div>
            )}

            {['ta', 'lecturer'].includes(form.role) && (
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                    <label className="block text-xs font-bold text-gray-500 mb-1.5 ml-1">담당 과목</label>
                    <select required className="w-full border-2 border-gray-200 focus:border-blue-400 outline-none p-3 rounded-xl bg-white font-bold transition-colors" value={form.subject} onChange={e => setForm({...form, subject: e.target.value})}>
                        <option value="" disabled hidden>과목을 선택해주세요</option>
                        {/* 회원 관리·반 과목과 같은 어휘를 씁니다.
                            예전에는 여기만 '기타' 가 있어서, 그걸 고른 강사는 어느 과목으로도
                            판정되지 않는 상태로 남았습니다. 사회는 아예 없었습니다.
                            가입 화면은 로그인 전이라 부서 설정을 읽을 수 없으므로 다섯 개를 모두 보여주고,
                            실제 확정은 회원 관리에서 합니다. */}
                        {MAIN_SUBJECTS.map(sub => <option key={sub} value={sub}>{sub}</option>)}
                    </select>
                </div>
            )}

            <div className="pt-2">
                <button type="submit" disabled={loading || !smsAuth.verified} className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold shadow-md hover:bg-blue-700 transition-colors disabled:opacity-50">
                    {loading ? <Loader className="animate-spin mx-auto" /> : '가입 신청 완료하기'}
                </button>
            </div>
        </form>
    );
};

const LoginView = ({ form, setForm, onLogin, isLoading, loginErrorModal, setLoginErrorModal }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isSignUpMode, setIsSignUpMode] = useState(false);
  const handleKeyDown = (e) => { if (e.key === 'Enter') onLogin(); };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-3xl shadow-xl p-8 border border-gray-100">
        {isSignUpMode ? (
            <SignUpForm onCancel={() => setIsSignUpMode(false)} setLoginErrorModal={setLoginErrorModal} />
        ) : (
            <>
                <div className="text-center mb-8">
                    <div className="bg-blue-600 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                        <img src={emblemWhite} alt="" draggable={false} className="w-11 h-auto select-none" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900">Imperial System</h1>
                    <p className="text-gray-500 mt-2">목동임페리얼학원 통합 관리 시스템</p>
                </div>
                <div className="space-y-5">
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1.5 ml-1">아이디</label>
                        <input type="text" placeholder="ID를 입력하세요" className="w-full border rounded-xl p-4 bg-gray-50 outline-none focus:border-blue-500 font-bold" value={form.id} onChange={e=>setForm({...form, id:e.target.value})}/>
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1.5 ml-1">비밀번호</label>
                        <div className="relative">
                            <input type={showPassword ? "text" : "password"} placeholder="비밀번호를 입력하세요" className="w-full border rounded-xl p-4 bg-gray-50 outline-none focus:border-blue-500 font-bold" value={form.password} onChange={e=>setForm({...form, password:e.target.value})} onKeyDown={handleKeyDown}/>
                            <button type="button" className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" onClick={() => setShowPassword(!showPassword)}>{showPassword ? <EyeOff size={24} /> : <Eye size={24} />}</button>
                        </div>
                    </div>
                    <button type="button" onClick={onLogin} className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold shadow-md transition hover:bg-blue-700 active:bg-blue-800 active:scale-[0.99] disabled:opacity-60 disabled:cursor-wait" disabled={isLoading}>
                        {isLoading ? <Loader className="animate-spin mx-auto" /> : '로그인'}
                    </button>
                    <div className="pt-2 text-center border-t border-gray-100">
                        <button type="button" onClick={() => setIsSignUpMode(true)} className="text-sm font-bold text-blue-600 hover:text-blue-800">새로 오셨나요? 회원가입 하기</button>
                    </div>
                </div>
            </>
        )}
      </div>
      {loginErrorModal.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-in fade-in">
            <div className="bg-white p-6 rounded-2xl max-w-sm w-full shadow-2xl text-center space-y-4">
                <div className="bg-red-50 p-4 rounded-full text-red-500 inline-block"><AlertCircle size={48} /></div>
                <h3 className="text-xl font-bold leading-relaxed whitespace-pre-wrap">{loginErrorModal.msg}</h3>
                <button className="w-full py-3 bg-gray-100 hover:bg-gray-200 rounded-xl font-bold" onClick={() => setLoginErrorModal({ isOpen: false, msg: '' })}>확인</button>
            </div>
         </div>
      )}
    </div>
  );
};

/* [대시보드] 첫 화면 — 오늘 요약(TodaySummary) → My 메뉴 → 전체 메뉴 아이콘 격자.
   11-02 개편: 예전에는 큰 인사 배너 + 메뉴를 높이 160px 카드로 다시 그린 것뿐이라
   휴대폰에서 화면 7.5장 길이에 정보가 없었다. 인사는 한 줄로 줄이고, 메뉴는 아이콘 격자로
   (설명은 사이드바와 같은 내용이라 뺐다 — 버튼 title 로만 남김). */

/* My 메뉴 — 자주 쓰는 메뉴를 사람마다 골라 대시보드 맨 위에 둔다(11-02 원장 결정: 하단 탭바 대신).
   ⚠ 지금은 이 기기의 브라우저에만 저장한다. 회원 문서에 저장하려면 firestore.rules 의
     users 본인 수정 허용 칸(hasOnly 목록)에 새 칸을 더해 규칙을 배포해야 해서, 그 결정 전까지는 기기별로 둔다.
     PC 와 휴대폰에서 각각 고르게 되는 것이 그 대가다. */
const MY_MENU_KEY = (uid) => `imperial_my_menu_${uid}`;
const loadMyMenu = (uid) => {
    try { const v = JSON.parse(localStorage.getItem(MY_MENU_KEY(uid)) || '[]'); return Array.isArray(v) ? v : []; }
    catch (e) { return []; }
};

const MenuIcon = ({ item, theme, isFamily, onClick, mark }) => {
    const SafeIcon = ((item.studentIcon && isFamily) ? item.studentIcon : item.icon) || Activity;
    const label = (item.studentName && isFamily) ? item.studentName : item.name;
    return (
        <button
            type="button"
            title={item.desc}
            onClick={onClick}
            className="group relative flex flex-col items-center gap-1.5 px-1 py-2 rounded-2xl transition hover:bg-white active:bg-slate-200/60 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
            <span className={`w-12 h-12 rounded-2xl flex items-center justify-center bg-gradient-to-br ${theme} text-white shadow-sm transition-transform group-hover:scale-105`}>
                <SafeIcon size={22} />
            </span>
            {mark && (
                <span className={`absolute top-1 left-1/2 ml-3 w-6 h-6 rounded-full border-2 border-white flex items-center justify-center shadow ${mark === 'on' ? 'bg-amber-400 text-white' : 'bg-slate-200 text-slate-500'}`}>
                    {mark === 'on' ? <Star size={12} fill="currentColor" /> : <Plus size={13} strokeWidth={3} />}
                </span>
            )}
            <span className="text-xs font-bold text-slate-700 text-center leading-snug break-keep line-clamp-2">{label}</span>
        </button>
    );
};

const ICON_GRID = 'grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-x-1 gap-y-3';

const Dashboard = ({ currentUser }) => {
    const navigate = useNavigate();
    const { loadingData } = useData() || { loadingData: false };
    const [myPaths, setMyPaths] = useState(() => (currentUser?.id ? loadMyMenu(currentUser.id) : []));
    const [editingMy, setEditingMy] = useState(false);

    const authorizedGroups = useMemo(() => {
        if (!currentUser?.role) return [];
        const dynamicGroups = getMenuGroups(currentUser);

        return dynamicGroups.map(group => {
            const authorizedItems = group.items.filter(item => {
                const hasRole = item.roles.includes(currentUser.role);
                const passCondition = item.condition ? item.condition(currentUser) : true;
                return hasRole && passCondition;
            });
            return { ...group, items: authorizedItems };
        }).filter(group => group.items.length > 0);
    }, [currentUser]);

    // 경로 → 메뉴(색 포함). 권한이 바뀌어 더는 못 쓰는 메뉴는 My 메뉴에서도 저절로 빠진다.
    const itemByPath = useMemo(() => {
        const m = new Map();
        authorizedGroups.forEach(g => g.items.forEach(item => { if (!m.has(item.path)) m.set(item.path, { item, theme: g.theme }); }));
        return m;
    }, [authorizedGroups]);
    const myItems = myPaths.map(p => itemByPath.get(p)).filter(Boolean);

    const toggleMy = (path) => {
        setMyPaths(prev => {
            const next = prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path];
            try { localStorage.setItem(MY_MENU_KEY(currentUser.id), JSON.stringify(next)); } catch (e) { /* 저장 공간이 막힌 브라우저 — 이번 접속 동안만 유지 */ }
            return next;
        });
    };

    if (loadingData || !currentUser) {
        return (
            <div className="flex flex-col justify-center items-center h-[50vh]">
                <Loader className="animate-spin text-indigo-500 mb-3" size={32} />
                <p className="text-slate-500 font-bold text-sm">불러오는 중...</p>
            </div>
        );
    }

    const today = new Date();
    const todayLabel = `${today.getMonth() + 1}월 ${today.getDate()}일 ${['일', '월', '화', '수', '목', '금', '토'][today.getDay()]}요일`;
    const isFamily = ['student', 'parent'].includes(currentUser.role);

    return (
        <div className="max-w-screen-2xl mx-auto space-y-5 md:space-y-7 animate-in fade-in pb-20">
            <div className="px-1">
                <p className="text-xs md:text-sm font-bold text-slate-400">{todayLabel}</p>
                <h1 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight">안녕하세요, {currentUser.name}님</h1>
            </div>

            <TodaySummary currentUser={currentUser} />

            <section className={editingMy ? 'rounded-2xl ring-2 ring-amber-300 bg-amber-50/60 p-2 -m-2' : ''}>
                <div className="flex items-center px-1 mb-2">
                    <h2 className="text-sm font-black text-slate-800 flex items-center gap-1"><Star size={14} className="text-amber-400" fill="currentColor" /> My 메뉴</h2>
                    <button type="button" onClick={() => setEditingMy(v => !v)}
                        className={`ml-auto px-3 py-2 rounded-lg text-xs font-bold ${editingMy ? 'bg-amber-500 text-white' : 'text-slate-500 hover:bg-white active:bg-slate-200'}`}>
                        {editingMy ? '완료' : '편집'}
                    </button>
                </div>
                {editingMy && <p className="text-xs font-bold text-amber-700 px-1 mb-2">아래 메뉴의 + 를 눌러 추가하고, ★ 를 눌러 뺍니다. 이 기기에 저장됩니다.</p>}
                {myItems.length > 0 ? (
                    <div className={ICON_GRID}>
                        {myItems.map(({ item, theme }) => (
                            <MenuIcon key={item.path} item={item} theme={theme} isFamily={isFamily}
                                mark={editingMy ? 'on' : null}
                                onClick={() => (editingMy ? toggleMy(item.path) : navigate(item.path))} />
                        ))}
                    </div>
                ) : !editingMy && (
                    <button type="button" onClick={() => setEditingMy(true)}
                        className="w-full py-4 rounded-2xl border-2 border-dashed border-slate-200 text-sm font-bold text-slate-400 hover:bg-white active:bg-slate-100">
                        자주 쓰는 메뉴를 골라 여기에 모아 두세요
                    </button>
                )}
            </section>

            <div className="space-y-5 md:space-y-6">
                {authorizedGroups.map((group, gIdx) => (
                    <section key={gIdx}>
                        <h2 className="text-sm font-black text-slate-500 px-1 mb-2">{group.title}</h2>
                        <div className={ICON_GRID}>
                            {group.items.map((item, iIdx) => (
                                <MenuIcon key={iIdx} item={item} theme={group.theme} isFamily={isFamily}
                                    mark={editingMy ? (myPaths.includes(item.path) ? 'on' : 'off') : null}
                                    onClick={() => (editingMy ? toggleMy(item.path) : navigate(item.path))} />
                            ))}
                        </div>
                    </section>
                ))}
            </div>
        </div>
    );
};

const AppLayout = ({ currentUser, handleLogout }) => {
  const { users } = useData();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const groupedMenus = useMemo(() => {
      const groups = {};
      const dynamicGroups = getMenuGroups(currentUser);
      dynamicGroups.forEach(group => {
          const items = group.items.filter(item => {
              const hasRole = item.roles.includes(currentUser.role);
              const passCondition = item.condition ? item.condition(currentUser) : true;
              return hasRole && passCondition;
          });
          
          if (items.length > 0) {
              groups[group.title] = items.map(item => ({
                  path: item.path,
                  label: (item.studentName && ['student', 'parent'].includes(currentUser.role)) ? item.studentName : item.name,
                  icon: (item.studentIcon && ['student', 'parent'].includes(currentUser.role)) ? item.studentIcon : item.icon,
              }));
          }
      });
      return groups;
  }, [currentUser]);

  const [expandedCategories, setExpandedCategories] = useState({});
  useEffect(() => {
      const initials = {};
      Object.keys(groupedMenus).forEach(cat => initials[cat] = true);
      setExpandedCategories(initials);
  }, [groupedMenus]);

  const toggleCategory = (category) => {
      setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden w-full">
      {isSidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 md:hidden animate-in fade-in duration-300" onClick={() => setIsSidebarOpen(false)}/>}
      
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r transform transition-transform duration-300 md:relative md:translate-x-0 flex flex-col ${isSidebarOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'}`}>
        <div className="p-6 border-b flex justify-between items-center shrink-0">
          {/* 모바일 머리글과 같은 모양 — 파란색은 엠블럼 사각형 하나에만 둡니다. */}
          <div className="flex items-center gap-2">
            <div className="bg-blue-600 w-8 h-8 rounded-lg flex items-center justify-center shadow-sm">
              <img src={emblemWhite} alt="" draggable={false} className="w-6 h-auto select-none" />
            </div>
            <h1 className="text-lg font-bold text-gray-900">Imperial</h1>
          </div>
          <button onClick={() => setIsSidebarOpen(false)} className="md:hidden p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"><X /></button>
        </div>
        
        <nav className="p-3 space-y-4 flex-1 overflow-y-auto custom-scrollbar">
            <button 
                onClick={() => { navigate('/dashboard'); setIsSidebarOpen(false); }} 
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${location.pathname === '/dashboard' ? 'bg-blue-50 text-blue-600 font-bold' : 'text-gray-700 hover:bg-gray-50 font-medium'}`}
            >
                <Home size={20} /> 대시보드 홈
            </button>

            {Object.entries(groupedMenus).map(([category, items]) => (
                <div key={category} className="space-y-1">
                    <button 
                        onClick={() => toggleCategory(category)}
                        className="w-full flex items-center justify-between px-4 py-2 text-xs font-black text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        {category}
                        {expandedCategories[category] ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
                    </button>
                    
                    {expandedCategories[category] && (
                        <div className="space-y-1 animate-in slide-in-from-top-2 duration-200">
                            {items.map((item) => {
                                const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                                const SafeIcon = item.icon || Activity;
                                return (
                                    <button 
                                        key={item.path} 
                                        onClick={() => { navigate(item.path); setIsSidebarOpen(false); }} 
                                        className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${isActive ? 'bg-blue-50 text-blue-600 font-bold shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}
                                    >
                                        <SafeIcon size={18} className={isActive ? 'text-blue-600' : 'text-gray-400'} /> 
                                        <span className="text-sm">{item.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            ))}
        </nav>
        
        <div className="p-4 border-t bg-white shrink-0 z-10 mt-auto">
            <div className="flex items-center gap-3 mb-4 px-2 p-2 rounded-xl border border-transparent">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center font-bold text-blue-600 uppercase">{currentUser?.name?.[0] || 'U'}</div>
                <div className="flex flex-col text-left flex-1">
                    <span className="font-bold text-sm text-gray-900 leading-tight">{currentUser?.name || '사용자'}</span>
                    <span className="text-xs text-gray-500 uppercase">
                        {currentUser?.role === 'admin_assistant' ? 'ADMIN ASSISTANT' : currentUser?.role}
                    </span>
                </div>
            </div>
            <button onClick={handleLogout} className="w-full flex items-center gap-2 text-red-500 hover:bg-red-50 p-2 rounded-lg font-bold transition-colors">
                <LogOut size={16}/> 로그아웃
            </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col h-full overflow-hidden w-full relative min-w-0">
        <header className="md:hidden shrink-0 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between shadow-sm z-30">
            <div className="flex items-center gap-2">
                <div className="bg-blue-600 w-8 h-8 rounded-lg flex items-center justify-center shadow-sm">
                    <img src={emblemWhite} alt="" draggable={false} className="w-6 h-auto select-none" />
                </div>
                <h1 className="text-lg font-bold text-gray-900">Imperial</h1>
            </div>
            <button onClick={() => setIsSidebarOpen(true)} className="p-2 text-gray-600 hover:bg-gray-100 rounded-xl transition-all active:scale-95" aria-label="메뉴 열기">
                <Menu size={24} />
            </button>
        </header>

        <main className="flex-1 overflow-y-auto overflow-x-hidden min-w-0 w-full bg-gray-50">
            {/* ⚠️ 예전에는 여기에 flex flex-col items-stretch 가 붙어 있었습니다.
                그런데 각 화면의 최상위 요소는 거의 모두 'max-w-*xl mx-auto' 형태입니다.
                CSS 규칙상 flex 항목의 좌우 margin 이 auto 이면 stretch(늘리기)가 꺼지고,
                항목이 '내용 크기'대로 커집니다. 그래서 내용이 넓은 화면(재무 대시보드,
                환경설정 등)은 최상위 컨테이너 자체가 화면보다 넓어졌고,
                main 의 overflow-x-hidden 에 잘려 오른쪽이 안 보였습니다.
                (진단 결과: 화면 384px인데 컨테이너가 671px)
                평범한 블록으로 두면 자식은 부모 폭을 넘지 않습니다. */}
            <div className="max-w-[1600px] w-full mx-auto px-3 sm:px-4 md:px-8 py-4 md:py-6">
                {/* 화면 하나가 터져도 앱 전체가 흰 화면이 되지 않게 막습니다.
                    주소가 바뀌면(resetKey) 오류 상태가 저절로 풀립니다. */}
                <ErrorBoundary label={location.pathname} resetKey={location.pathname}>
                <Suspense fallback={<div className="h-full flex items-center justify-center min-h-[50vh]"><Loader className="animate-spin text-blue-600" size={40} /></div>}>
                    <Routes>
                        <Route path="/dashboard" element={<Dashboard currentUser={currentUser} />} />
                        <Route path="/my-day" element={currentUser.role === 'student' ? <StudentDashboard currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/consult" element={['admin', 'admin_assistant', 'lecturer'].includes(currentUser.role) ? <ConsultationManager /> : <Navigate to="/dashboard" replace />} />
                        {['admin', 'lecturer', 'admin_assistant'].includes(currentUser.role) && <Route path="/attendance" element={<AttendanceManager currentUser={currentUser} />} />}
                        <Route path="/lectures" element={ ['admin', 'admin_assistant'].includes(currentUser.role) ? <AdminLectureManager /> : currentUser.role === 'lecturer' ? <LecturerDashboard currentUser={currentUser} /> : <StudentClassroom currentUser={currentUser} /> } />
                        <Route path="/call-records" element={currentUser.role === 'admin' ? <CallRecords currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/messages" element={['admin', 'admin_assistant'].includes(currentUser.role) ? <MessageCenter currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/users" element={['admin', 'admin_assistant'].includes(currentUser.role) ? <UserManager currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/settings" element={currentUser.role === 'admin' ? <SettingsManager currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/financial-dashboard" element={currentUser.role === 'admin' ? <FinancialDashboard currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        {['admin', 'lecturer', 'ta', 'admin_assistant'].includes(currentUser.role) && <Route path="/expense" element={<ExpenseManager currentUser={currentUser} />} />}
                        <Route path="/strategy" element={<SchoolStrategy currentUser={currentUser} />} />
                        
                        {/* 🚀 CTO Patch: 온톨로지 라우트 연결 (어느 권한이든 로그인된 유저면 확인 가능) */}
                        <Route path="/ontology" element={<OntologyMap />} />

                        <Route path="/clinic" element={<ClinicDashboard currentUser={currentUser} users={users} mode="clinic" />} />
                        <Route path="/clinic-tasks" element={['admin', 'lecturer', 'ta', 'admin_assistant'].includes(currentUser.role) ? <ClinicTaskManager currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        {currentUser.role === 'admin_assistant' && <Route path="/work-schedule" element={<ClinicDashboard currentUser={currentUser} users={users} mode="work_schedule" />} />}
                        <Route path="/pickup" element={<PickupRequest currentUser={currentUser} />} />
                        {['admin', 'lecturer', 'ta', 'admin_assistant'].includes(currentUser.role) && <Route path="/exams" element={<ExamArchive currentUser={currentUser} />} />}
                        {['admin', 'lecturer', 'ta', 'admin_assistant'].includes(currentUser.role) && <Route path="/textbooks" element={<TextbookManager currentUser={currentUser} />} />}
                        {['admin', 'lecturer', 'ta', 'admin_assistant'].includes(currentUser.role) && <Route path="/homework" element={<HomeworkManager currentUser={currentUser} />} />}
                        <Route path="/payroll-mgmt" element={<PayrollManager currentUser={currentUser} users={users} viewMode="management" />} />
                        <Route path="/payroll-check" element={<PayrollManager currentUser={currentUser} users={users} viewMode="personal" />} />
                        <Route path="/exam-diagnostics" element={['admin', 'lecturer', 'admin_assistant', 'ta'].includes(currentUser.role) ? <ExamDiagnosticInput currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/report/:diagnosticId" element={<ReportWrapper />} />
                        <Route path="/my-exams" element={['student', 'parent'].includes(currentUser.role) ? <StudentExamList currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/voca" element={['student', 'parent'].includes(currentUser.role) ? <StudentVocaDaily currentUser={currentUser} /> : <VocaManager currentUser={currentUser} />} />
                        <Route path="/voca-challenge" element={['student', 'admin', 'admin_assistant', 'lecturer', 'ta'].includes(currentUser.role) ? <VocaChallenge currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        {/* 원장 지시(08-30): 검수 전이라 관리자만 — 검수 후 student·parent 복원 */}
                        <Route path="/navigator" element={['admin'].includes(currentUser.role) ? <CollegeNavigator currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/navigator/:studentId" element={['admin'].includes(currentUser.role) ? <CollegeNavigator currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        {/* 📊 성적 분석 — 로컬 점검용 이식 (관리자 전용, 사이드바 항목 = 기능) */}
                        <Route path="/grade-center" element={['admin'].includes(currentUser.role) ? <GradeAnalysisHub /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/grade-center/:tab" element={['admin'].includes(currentUser.role) ? <GradeAnalysisHub /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/universe" element={['student', 'parent', 'admin', 'admin_assistant', 'lecturer', 'ta'].includes(currentUser.role) ? <AcademyUniverse currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        
                        <Route path="/recruitment" element={['admin', 'admin_assistant'].includes(currentUser.role) ? <RecruitmentManager /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/academic-calendar" element={['admin', 'admin_assistant'].includes(currentUser.role) ? <AcademicCalendarManager /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/care-report" element={<CareReportManager currentUser={currentUser} />} />
                        {/* 교재 주문: 데스크(관리자·행정조교)와 학생·학부모만. 강사·조교는 주문(학부모 연락처 포함)을 볼 필요가 없다. */}
                        <Route path="/books" element={['admin', 'admin_assistant', 'student', 'parent'].includes(currentUser.role) ? <BookOrders currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />
                        {/* 교재 발주 관리: 지금은 관리자만 (나중에 데스크·행정조교에게 열 예정) */}
                        <Route path="/book-purchases" element={currentUser.role === 'admin' ? <BookPurchases currentUser={currentUser} /> : <Navigate to="/dashboard" replace />} />

                        <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    </Routes>
                </Suspense>
                </ErrorBoundary>
            </div>
        </main>
      </div>
    </div>
  );
};

/* 상담 키오스크의 가상 사용자. 컴포넌트 밖에 두어야 화면을 다시 그릴 때마다
   새 객체가 되어 DataProvider 가 구독을 끊었다 다시 거는 일이 없다. */
const KIOSK_USER = { role: 'admin_assistant', name: '상담용 태블릿 기기' };

const AppContent = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginForm, setLoginForm] = useState({ id: '', password: '' });
  const [loginProcessing, setLoginProcessing] = useState(false);
  const [loginErrorModal, setLoginErrorModal] = useState({ isOpen: false, msg: '' });

  const navigate = useNavigate();

  /* 🔒 세션 복원은 Firebase Auth 세션을 기준으로 합니다.
     이제 모든 Firestore 접근이 인증을 요구하므로, sessionStorage만 남아 있고
     Auth 세션이 끊긴 상태로 앱에 들어가면 화면 전체가 권한 오류로 깨집니다.
     따라서 Auth 세션이 없으면 저장된 프로필도 함께 버립니다. */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      const savedUser = sessionStorage.getItem('imperial_user');
      if (firebaseUser && savedUser) {
        try {
          /* 이미 로그인된 상태로 앱을 다시 열었을 때도 역할 토큰을 최신화합니다.
             역할이 바뀌었거나 토큰에 역할이 없던 사용자를 자동으로 복구합니다. */
          try { await firebaseUser.getIdToken(true); } catch (e) { console.warn('토큰 갱신 실패:', e); }

          const parsed = JSON.parse(savedUser);
          if (parsed.id) parsed.id = String(parsed.id).toLowerCase();
          setCurrentUser(parsed);
        } catch (e) {
          sessionStorage.removeItem('imperial_user');
          setCurrentUser(null);
        }
      } else {
        sessionStorage.removeItem('imperial_user');
        setCurrentUser(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  /* 🔒 로그인은 반드시 Firebase Auth 세션을 확보한 뒤에 프로필을 읽습니다.
     1) 정상 계정  : Auth로 바로 로그인
     2) 과거 계정  : 서버(legacyLoginBridge)가 대조 후 Auth 계정을 만들어 주고
                     Firestore의 평문 비밀번호를 즉시 삭제 → 자동 이관
     어느 경로든 최종적으로 Auth 세션이 있어야만 앱에 들어옵니다. */
  const handleLogin = async () => {
      if (!loginForm.id || !loginForm.password) { setLoginErrorModal({ isOpen: true, msg: '정보를 입력하세요.' }); return; }
      setLoginProcessing(true);
      try {
          const rawId = loginForm.id.trim();
          const safeId = toSafeId(rawId);
          let docId = safeId;

          /* ⚠️ 과거 호환: Firebase Auth는 비밀번호가 6자리 이상이어야 합니다.
             예전 코드는 6자리 미만 비밀번호를 '0'으로 채워서 Auth에 저장했기 때문에,
             그런 계정은 사용자가 입력한 원본으로는 로그인이 되지 않습니다.
             따라서 원본과 패딩본을 모두 시도해야 기존 사용자가 잠기지 않습니다. */
          const pwCandidates = [...new Set([
              loginForm.password,
              loginForm.password.length < 6 ? loginForm.password.padEnd(6, '0') : loginForm.password
          ])];

          let signedIn = false;
          for (const pw of pwCandidates) {
              try {
                  await signInWithEmailAndPassword(auth, `${safeId}@imperial.com`, pw);
                  signedIn = true;
                  break;
              } catch (e) { /* 다음 후보로 계속 */ }
          }

          if (!signedIn) {
              const bridge = httpsCallable(functions, 'legacyLoginBridge');
              const res = await bridge({ userId: rawId, password: loginForm.password });
              if (!res?.data?.token) throw new Error('AUTH_FAILED');
              docId = res.data.docId || safeId;
              await signInWithCustomToken(auth, res.data.token);
          }

          /* 🔑 로그인 토큰에 담긴 역할(role) 정보를 최신으로 갱신합니다.
             보안 규칙은 이 값으로 권한을 판정하며, 이 값이 없으면 규칙이 매번
             사용자 문서를 조회하다 조회 한도에 걸립니다.
             (학생이 클리닉 시간대를 여러 개 신청할 때 권한 오류가 나던 원인) */
          try { await auth.currentUser?.getIdToken(true); } catch (e) { console.warn('토큰 갱신 실패:', e); }

          const userDocRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'users', docId);
          const userDoc = await getDoc(userDocRef);
          if (!userDoc.exists()) {
              await signOut(auth);
              setLoginErrorModal({ isOpen: true, msg: '로그인 실패: 시스템에 등록된 계정 정보가 없습니다.' });
              return;
          }

          const docData = userDoc.data();
          if (docData.status === 'pending') {
              await signOut(auth);
              setLoginErrorModal({ isOpen: true, msg: '가입 승인이 대기 중인 계정입니다.\n\n학원 데스크에서 승인을 완료해야 로그인이 가능합니다.' });
              return;
          }

          // 혹시 남아 있을 수 있는 비밀번호 필드는 브라우저 세션에 절대 담지 않습니다.
          const { password, ...safeProfile } = docData;
          const userData = { ...safeProfile, id: docId, authUid: auth.currentUser?.uid || safeProfile.authUid || null };

          /* 마지막 로그인 시각(lastLogin)은 더 기록하지 않는다(11-03 원장 결정 — 쓸 계획 없음).
             기록할 때마다 회원 문서가 바뀌어, 열려 있는 교직원 화면 전부가 그 문서를 다시 받고
             회원 문서 트리거 함수 3개가 매번 깨어났다. */

          setCurrentUser(userData);
          sessionStorage.setItem('imperial_user', JSON.stringify(userData));
          navigate('/dashboard');
      } catch (e) {
          console.error('Login Error:', e);
          try { await signOut(auth); } catch (_) {}
          const msg = e?.code === 'functions/resource-exhausted'
              ? (e.message || '로그인 시도가 많습니다. 잠시 후 다시 시도해주세요.')
              : '로그인 실패: 아이디 또는 비밀번호를 다시 확인해 주세요.';
          setLoginErrorModal({ isOpen: true, msg });
      }
      finally { setLoginProcessing(false); }
  };

  const handleLogout = async () => { 
      try { await signOut(auth); } catch (e) { console.error("Sign Out Error:", e); }
      sessionStorage.removeItem('imperial_user'); 
      setCurrentUser(null); 
      navigate('/'); 
  };

  if (loading) return <div className="h-screen flex items-center justify-center"><Loader className="animate-spin text-blue-600" size={40} /></div>;

  return (
    <Routes>
      <Route path="/kiosk/consult" element={
        <Suspense fallback={<div className="h-screen flex items-center justify-center bg-gray-50"><Loader className="animate-spin text-blue-600" size={40} /></div>}>
          <DataProvider currentUser={KIOSK_USER}>
            <div className="w-full min-h-screen bg-gray-50 p-4 sm:p-6 md:p-10">
              <ConsultationManager isKiosk={true} />
            </div>
          </DataProvider>
        </Suspense>
      } />
      
      <Route path="*" element={
        !currentUser ? (
          <LoginView form={loginForm} setForm={setLoginForm} onLogin={handleLogin} isLoading={loginProcessing} loginErrorModal={loginErrorModal} setLoginErrorModal={setLoginErrorModal} />
        ) : (
          <DataProvider currentUser={currentUser}>
            <AppLayout currentUser={currentUser} handleLogout={handleLogout} />
          </DataProvider>
        )
      } />
    </Routes>
  );
};

const App = () => <Router><AppContent /></Router>;
export default App;